import {
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  TextInput,
  FormSelectOption,
  Title,
} from '@patternfly/react-core'
import parsePhoneNumberLib, { AsYouType } from 'libphonenumber-js'
import * as React from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { getPhoneTypeLabel } from '@app/domain/patient/patient.helpers'
import type {
  IPatientEmailAddress,
  IPatientPhoneNumber,
} from '@app/domain/patient/patient.interface'
import { PhoneNumberTypeId } from '@app/domain/patient/patient.interface'
import { emailValidator, ValidationStatusId } from '@app/lib/validators'
import * as messages from '@app/state/patients/messages'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  onCancel: () => void
  onAddContactInformation: (
    email: IPatientEmailAddress,
    phone: IPatientPhoneNumber,
    phoneType: PhoneNumberTypeId,
  ) => void
}

export const AddContactCard = ({
  onCancel,
  onAddContactInformation,
}: Props) => {
  const [email, setEmail] = useState<IPatientEmailAddress>('')
  const [emailValidation, setEmailValidation] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )
  const [emailValidatorText, setEmailValidatorText] = useState<string>('')

  const [phone, setPhone] = useState<IPatientPhoneNumber>('')
  const [phoneValidation, setPhoneValidation] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )
  const [phoneValidatorText, setPhoneValidatorText] = useState<string>('')

  const [phoneType, setPhoneType] = useState<PhoneNumberTypeId>(
    PhoneNumberTypeId.null,
  )
  const [phoneTypeValidation, setPhoneTypeValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  const [isFormDirty, setIsFormDirty] = useState<boolean>(false)
  const prevPhoneRef = useRef<string>('')

  const validateForm = useMemo(() => {
    if (!isFormDirty) return false

    let valid = true

    const emailValid = email.trim() === '' || emailValidator(email.trim())
    const parsedPhone = parsePhoneNumberLib(phone.trim(), 'CA')
    const phoneValid = phone.trim() === '' || parsedPhone?.isValid()

    if (!emailValid && !phoneValid) {
      valid = false
      setEmailValidation(ValidationStatusId.Error)
      setPhoneValidation(ValidationStatusId.Error)
      setEmailValidatorText(messages.missingEmailAndPhoneErrorMessage)
      setPhoneValidatorText(messages.missingEmailAndPhoneErrorMessage)
    }

    if (phone.trim() !== '') {
      if (!parsedPhone?.isValid()) {
        valid = false
        setPhoneValidation(ValidationStatusId.Error)
        setPhoneValidatorText('Invalid phone number.')
      }

      if (phoneType === PhoneNumberTypeId.null) {
        valid = false
        setPhoneTypeValidation(ValidationStatusId.Error)
      }
    } else {
      setPhoneType(PhoneNumberTypeId.null)
    }

    if (email.trim() !== '' && !emailValidator(email.trim())) {
      valid = false
      setEmailValidation(ValidationStatusId.Error)
      setEmailValidatorText(messages.emailValidatorText)
    }

    return valid
  }, [isFormDirty, email, phone, phoneType])

  const handleAddContactInformation = useCallback(() => {
    if (validateForm) {
      const parsed = parsePhoneNumberLib(phone, 'CA')
      const normalizedPhone = parsed?.isValid() ? parsed.format('E.164') : phone

      onAddContactInformation?.(email, normalizedPhone, phoneType)
    }
  }, [validateForm, onAddContactInformation, email, phone, phoneType])

  const handleSetEmail = useCallback(
    (newEmail: IPatientEmailAddress) => {
      setIsFormDirty(true)
      if (newEmail.trim() === '') {
        if (phone.trim() === '') {
          setEmailValidation(ValidationStatusId.Error)
          setEmailValidatorText(messages.emailValidatorNoPhone)
        }
      } else {
        if (emailValidator(newEmail) === false) {
          setEmailValidation(ValidationStatusId.Error)
          setEmailValidatorText(messages.emailValidatorText)
        } else {
          setEmailValidation(ValidationStatusId.Success)
          if (phone.trim() === '') {
            setPhoneValidation(ValidationStatusId.Default)
          }
        }
      }
      setEmail(newEmail)
    },
    [phone],
  )

  const handlePhoneBlur = useCallback(() => {
    setPhone((prev) => {
      const formatted = new AsYouType('CA').input(prev.trim())

      prevPhoneRef.current = formatted

      if (formatted.trim() === '') {
        setPhoneType(PhoneNumberTypeId.null)
        setPhoneTypeValidation(ValidationStatusId.Default)
        setPhoneValidation(ValidationStatusId.Default)
        setPhoneValidatorText(undefined)

        if (email.trim() === '') {
          setPhoneValidation(ValidationStatusId.Error)
          setPhoneValidatorText(messages.phoneValidatorNoEmail)
        }
      } else {
        const parsed = parsePhoneNumberLib(formatted, 'CA')
        if (!parsed?.isValid()) {
          setPhoneValidation(ValidationStatusId.Error)
          setPhoneValidatorText('Invalid phone number.')
        } else {
          setPhoneValidation(ValidationStatusId.Success)
          setPhoneValidatorText(undefined)
          if (email.trim() === '')
            setEmailValidation(ValidationStatusId.Default)
        }
      }

      return formatted
    })
  }, [email])

  const handleSetPhone = useCallback(
    (newPhone: IPatientPhoneNumber, e?: React.FormEvent<HTMLInputElement>) => {
      setIsFormDirty(true)

      const inputElement = e?.currentTarget
      const selStart = inputElement?.selectionStart ?? newPhone.length
      const selEnd = inputElement?.selectionEnd ?? newPhone.length
      const caretAtEnd =
        selStart === newPhone.length && selEnd === newPhone.length

      const wasDeletion = newPhone.length < (prevPhoneRef.current?.length ?? 0)

      const nextValue =
        !wasDeletion && caretAtEnd
          ? new AsYouType('CA').input(newPhone)
          : newPhone

      setPhone(nextValue)
      prevPhoneRef.current = nextValue

      const trimmed = nextValue.trim()
      if (trimmed === '') {
        setPhoneType(PhoneNumberTypeId.null)
        setPhoneTypeValidation(ValidationStatusId.Default)
        setPhoneValidation(ValidationStatusId.Default)
        setPhoneValidatorText(undefined)

        if (email.trim() === '') {
          setPhoneValidation(ValidationStatusId.Error)
          setPhoneValidatorText(messages.phoneValidatorNoEmail)
        }
        return
      }

      const parsed = parsePhoneNumberLib(trimmed, 'CA')
      if (!parsed?.isValid()) {
        setPhoneValidation(ValidationStatusId.Error)
        setPhoneValidatorText('Invalid phone number.')
      } else {
        setPhoneValidation(ValidationStatusId.Success)
        setPhoneValidatorText(undefined)
        if (email.trim() === '') setEmailValidation(ValidationStatusId.Default)
      }
    },
    [email],
  )

  const handleSetPhoneType = useCallback(
    (newPhoneType) => {
      setIsFormDirty(true)
      if (newPhoneType === PhoneNumberTypeId.null) {
        if (phone.trim() !== '') {
          setPhoneTypeValidation(ValidationStatusId.Error)
        }
      } else {
        setPhoneTypeValidation(ValidationStatusId.Default)
      }
      setPhoneType(newPhoneType)
    },
    [phone],
  )

  return (
    <Flex direction={{ default: 'column' }}>
      <FlexItem>
        <Flex direction={{ default: 'row' }}>
          <FlexItem>
            <Title headingLevel="h2" size="lg">
              Add Contact Information
            </Title>
          </FlexItem>
          <FlexItem>
            <SmallButton variant="secondary" onClick={onCancel}>
              Cancel
            </SmallButton>
          </FlexItem>
          <FlexItem>
            <SmallButton
              variant="primary"
              isDisabled={!validateForm}
              onClick={handleAddContactInformation}
            >
              Save
            </SmallButton>
          </FlexItem>
        </Flex>
      </FlexItem>

      <Form isHorizontal>
        <FormGroup
          label="Email Address"
          fieldId="email"
          helperTextInvalid={emailValidatorText}
          validated={emailValidation}
        >
          <TextInput
            value={email}
            type="text"
            name="email"
            onChange={handleSetEmail}
            validated={emailValidation}
          />
        </FormGroup>
        <FormGroup
          label="Phone Number"
          fieldId="phone"
          helperTextInvalid={phoneValidatorText}
          validated={phoneValidation}
        >
          <TextInput
            value={phone}
            type="text"
            name="phone"
            onChange={handleSetPhone}
            onBlur={handlePhoneBlur}
            validated={phoneValidation}
          />
        </FormGroup>
        <FormGroup
          label="Phone Type"
          fieldId="phone-type"
          helperTextInvalid="Phone Type is required"
          validated={phoneTypeValidation}
        >
          <FormSelect
            value={phoneType}
            validated={phoneTypeValidation}
            onChange={handleSetPhoneType}
          >
            {Object.entries(PhoneNumberTypeId).map(([key, val]) => (
              <FormSelectOption
                key={key}
                value={PhoneNumberTypeId[key]}
                label={getPhoneTypeLabel(val)}
              />
            ))}
          </FormSelect>
        </FormGroup>
      </Form>
    </Flex>
  )
}
